import type { ServiceContractDto } from '@api/machines'
import { formatPriceAndCurrency } from '@common/utils'
import { MolsonAutocomplete, MolsonTextField } from '@components/reusable'
import { molsonUseTheme } from '@theme/customMuiExports'

type ServiceContractAutoCompleteProps = {
  options: ServiceContractDto[]
  selectedOption: ServiceContractDto | string
  sectionError?: boolean
  isArchived?: boolean
  placeholder: string
  displayPriceDifference?: number
  onChange: (optionId: number | null) => void
}

export const ServiceContractAutocomplete = ({
  options,
  selectedOption,
  sectionError,
  isArchived,
  placeholder,
  displayPriceDifference,
  onChange
}: ServiceContractAutoCompleteProps) => {
  const theme = molsonUseTheme()

  const getServiceContractLabel = (serviceContract?: ServiceContractDto) => {
    if (!serviceContract) {
      return ''
    }

    return `${serviceContract.hours}hrs (+${formatPriceAndCurrency(
      serviceContract.dnp as number
    )})`
  }

  const displayedSelectedValue = () => {
    if (selectedOption === '') {
      return selectedOption
    }

    const label = selectedOption as ServiceContractDto
    return `${label.hours}hrs (+${formatPriceAndCurrency(label.dnp)})`
  }

  return (
    <MolsonAutocomplete
      dataTestId="service-contract-autocomplete"
      options={options}
      getOptionLabel={(serviceContract: ServiceContractDto | string) => {
        if (typeof serviceContract === 'string') {
          return serviceContract
        }

        return getServiceContractLabel(serviceContract)
      }}
      onChange={(_, value) =>
        onChange((value as ServiceContractDto)?.id ?? null)
      }
      renderInput={params => (
        <MolsonTextField
          {...params}
          placeholder={placeholder}
          error={sectionError || isArchived}
          inputProps={{
            ...params.inputProps,
            value: displayedSelectedValue()
          }}
        />
      )}
      sx={{
        ...(isArchived && {
          '.MuiAutocomplete-input': {
            color: theme.palette.primary.error,
            textDecoration: 'line-through'
          },
          color: theme.palette.primary.error,
          textDecoration: 'line-through'
        }),
        width: displayPriceDifference !== undefined ? '100%' : '50%'
      }}
      value={selectedOption}
    />
  )
}
